import React, { useContext, useState } from 'react'
import VisibilityContext from 'src/provider/state-manager/visibilityProvider'
import AppInfoContext from 'src/provider/state-manager/appInfoProvider'
import UserContext from 'src/provider/state-manager/userProvider'
import apiCaller from '../interceptor'
import utility, { catchErrMsg, erroMessage, requestMessage, loglite } from 'src/utils/utility'
import { appSettings, url } from 'src/provider/config/constant'
import { AccountUpgrade, AuthModel, MFB_HostName, OtpAuthModel, TransactionPayload } from 'src/model'
import { v4 as uuidv4 } from 'uuid';
import helpers from 'src/utils/helpers'
import { 
    additionalAccount, identityMock, mockAccountOfficer, 
    mockAccountOfficerRating, mockBeneficiaries, mockNotifications, 
    mockScheduledTransfers, mockTransactionDetails 
} from 'src/utils/mock-data'
import access from 'src/utils/localAccess'

const ApiContext = React.createContext<any>(null)

export const ApiProvider = (props: any) => {
    const {loader, notifier} = useContext(VisibilityContext)
    const {info, setInfoProperty, logout} = useContext(AppInfoContext)
    const {user, setUserProperty} = useContext(UserContext)

    const institutionData = {
        ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode || info.institutionData?.id,
        terminalType: 0
    }

    async function validateNewCustomerBvn (bvn: string, dateOfBirth: string) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Onboarding/ValidateNewCustomer', {
                ...institutionData,
                deviceId: '',
                bvn,
                dateOfBirth
            })
            loader(false)

            loglite({data}, 'validateNewCustomerBvn API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(`OTP has been sent to your registered number ${data.data?.phoneNo}`, 'OTP Status', 'success')
                setUserProperty('onboardingInput', {...user.onboardingInput, bvn, dateOfBirth, phoneNo: data.data?.phoneNo})
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function validateReturningCustomerBvn (payload: any) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Onboarding/ValidateReturningCustomer', {
                ...institutionData,
                ...payload,
                deviceId: ''
            })
            loader(false)

            loglite({data}, 'validateReturningCustomerBvn API')

            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(`OTP has been sent to your registered number ${data.data?.phoneNo}`, 'OTP Status', 'success')
                setUserProperty('onboardingInput', {...user.onboardingInput, ...payload, phoneNo: data.data?.phoneNo, isOrganizational:data?.data?.isOrganizationnalAccount})
                return true
            }
        }
        catch (err: any) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            if (err?.response?.data?.responseCode === '1033') {
                return { isV1Customer: true }
            }
            return null
        }
    }

    async function validateNonNGNewCustomer (email: string) {
        try {
            loader(true)
            const {data} = await apiCaller.post('NonNigerianInstitution/InitializeAccountOpening', {
                ...institutionData,
                deviceId: '',
                email
            })
            loader(false)

            loglite({data}, 'validateNonNGNewCustomer API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(`OTP has been sent to your registered email ${data.data?.email}`, 'OTP Status', 'success')
                setUserProperty('onboardingInput', {...user.onboardingInput, email, phoneNo: data.data?.phoneNo})
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function validateNonNGReturningCustomer (accountNumber: string, phoneNumberOrEmail: string) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Onboarding/ValidateAccountNumber', {
                ...institutionData,
                deviceId: '',
                accountNumber,
                phoneNumberOrEmail
            })
            loader(false)

            loglite({data}, 'validateNonNGReturningCustomer API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(`OTP has been sent to your registered number ${data.data?.phoneNo}`, 'OTP Status', 'success')
                setUserProperty('onboardingInput', {...user.onboardingInput, accountNumber, bvn: accountNumber, phoneNumberOrEmail, phoneNo: data.data?.phoneNo})
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getNewCustomerData (otp: string) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Onboarding/GetNewCustomerData', {
                ...institutionData,
                deviceId: '',
                bvn: user.onboardingInput?.bvn,
                otp: helpers.hashString(otp),
            })
            loader(false)

            loglite({data}, 'getNewCustomerData API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                setUserProperty('userPreAuthInfo', {...user.userPreAuthInfo, ...data.data})
                setUserProperty('onboardingInput', {...user.onboardingInput, otp})
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getReturningCustomerData (otp: string) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Onboarding/GetReturningCustomer', {
                ...institutionData,
                deviceId: '',
                bvn: user.onboardingInput?.bvn,
                accountNumber: user.onboardingInput?.accountNumber,
                otp: helpers.hashString(otp)
            })
            loader(false)

            loglite({data}, 'getReturningCustomerData API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                setUserProperty('userPreAuthInfo', {...user.userPreAuthInfo, ...data.data})
                setUserProperty('onboardingInput', {...user.onboardingInput, otp})
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getNonNGNewCustomerData (otp: string) {
        try {
            loader(true)
            const {data} = await apiCaller.post('NonNigerianInstitution/ValidateAccountOpeningOTP', {
                ...institutionData,
                deviceId: '',
                email: user.onboardingInput?.email,
                otp: helpers.hashString(otp),
            })
            loader(false)

            loglite({data}, 'getNonNGNewCustomerData API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                setUserProperty('userPreAuthInfo', {...user.userPreAuthInfo, ...data.data})
                setUserProperty('onboardingInput', {...user.onboardingInput, otp})
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function verifyIdentity (payload: any) {
        payload.identificationType = Number(payload.identificationType)
        
        // For testing mode, we are using a default image to test
        if (payload.identificationID === identityMock.ID && payload.identificationType == identityMock.type) {
            payload.identificationImage = identityMock.IMAGE
        } 

        try {
            loader(true)
            const {data} = await apiCaller.post('Onboarding/IdentityVerification', {
                ...institutionData,
                // bvn: user.onboardingInput?.bvn,
                // otp: helpers.hashString(user.onboardingInput?.otp),
                ...payload
            })
            loader(false)

            loglite({data}, 'verifyIdentity API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data || 'Identity verification failed'))
                return false
            }
            else return true
        }
        catch (err) {
            loader(false)
            console.log({err})
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getSecurityQuestions () {
        try {
            loader(true)
            const {data} = await apiCaller.get('Onboarding/GetSecurityQuestions')
            loader(false)

            loglite({data}, 'getSecurityQuestions API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function onboardCustomer (info?: any) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Onboarding/OnboardCustomer', {
                ...institutionData,
                deviceId: '',
                authenticationType: 0,
                ...user.onboardingInput,
                maritalStatus: Number(user.onboardingInput?.maritalStatus),
                gender: Number(user.onboardingInput?.gender),
                identificationType: user.onboardingInput?.identificationType ? Number(user.onboardingInput?.identificationType) : 0,
                informationSource: user.onboardingInput?.informationSource ? Number(user.onboardingInput?.informationSource) : 0,
                referral: user.onboardingInput?.referral ? Number(user.onboardingInput?.referral) : 0,
                accountOpeningTrackingRef: user.onboardingInput?.isNewCustomer ? uuidv4() : null,
                otp: helpers.hashString(user.onboardingInput?.otp),
                password: helpers.hashString(user.onboardingInput?.password),
                pin: helpers.hashString(user.onboardingInput?.pin),
                securityQuestion1: user.onboardingInput?.securityQuestion1 || info?.securityQuestion1,
                securityQuestion2: user.onboardingInput?.securityQuestion2 || info?.securityQuestion2,
                securityAnswer1: helpers.hashString(user.onboardingInput?.securityAnswer1 || info?.securityAnswer1),
                securityAnswer2: helpers.hashString(user.onboardingInput?.securityAnswer2 || info?.securityAnswer2)
            })
            loader(false)

            loglite({data}, 'onboardCustomer API')
            if (data.responseCode != '00' || !data.data?.isOnboardingSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return true
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function onboardNonNGNewCustomer (info?: any) {
        try {
            loader(true)
            const {data} = await apiCaller.post('NonNigerianInstitution/CompleteAccountOpening', {
                ...institutionData,
                deviceId: '',
                authenticationType: 0,
                ...user.onboardingInput,
                maritalStatus: Number(user.onboardingInput?.maritalStatus),
                gender: Number(user.onboardingInput?.gender),
                identificationType: user.onboardingInput?.identificationType ? Number(user.onboardingInput?.identificationType) : 0,
                informationSource: user.onboardingInput?.informationSource ? Number(user.onboardingInput?.informationSource) : 0,
                referral: user.onboardingInput?.referral ? Number(user.onboardingInput?.referral) : 0,
                transactionTrackingRef: uuidv4(),
                otp: helpers.hashString(user.onboardingInput?.otp),
                agreementDate: `${new Date().toISOString()}`,
            })
            loader(false)

            loglite({data}, 'onboardNonNGNewCustomer API')
            if (data.responseCode != '00' || !data.data?.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return true
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function login (payload: AuthModel) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Profile/Login', {
                ...institutionData,
                deviceId: '',
                username: payload.username,
                password: helpers.hashString(payload.password)
            })
            loader(false)

            loglite({data}, 'login API')
            const info = {...data.data}
            if (data.responseCode != '01') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                setInfoProperty('otpPhoneNo', data.data?.otpPhoneNumber)
                notifier.show(data.responseDescription, null, 'success', false)
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function authenticateLoginOtp (payload: OtpAuthModel) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Profile/AuthenticateLoginOTP', {
                ...institutionData,
                deviceID: '',
                username: payload.username,
                password: helpers.hashString(payload.password),
                otp: helpers.hashString(payload.otp)
            })
            loader(false)

            loglite({data}, 'authenticateLoginOtp API')
            const respData = {...data.data}
            if (data.responseCode != '00' || !respData?.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                setInfoProperty('authToken', respData?.authToken)
                delete respData.authToken
                delete respData.isSuccessful
                localStorage.setItem('userInfo', JSON.stringify(respData));
                setUserProperty('userInfo', {...user.userInfo, ...respData})
                setUserProperty('accountInfo', [...respData.customerAccounts.accountInformation])
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }
   
    async function getDashboardData () {
        try {
            loader(true)
            const {data} = await apiCaller.post('Account/GetProfileDashboardData', {
                ...institutionData,
                profileID: user.userInfo?.profileID    
            })
            loader(false)

            loglite({data}, 'getDashboardData API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                const accountWithBalanceDetails = []
                for (let account of data.data) { accountWithBalanceDetails.push(account?.accountBalance) }
                await setUserProperty('dashboardData', data.data)
                await setUserProperty('accountWithBalanceDetails', accountWithBalanceDetails)
                return data.data
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getTransactions (payload: TransactionPayload) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Account/GetMiniStatement', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode || institutionData.ibInstitutionCode,
                profileID: user.userInfo?.profileID,
                ...payload 
            })
            loader(false)

            loglite({data}, 'getTransactions API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                let isEmailSent = false
                if (data.message === appSettings.LONG_DURATION_STATEMENT_MSG) {
                    isEmailSent = true
                    notifier.show('Your Account Statement has been sent to your email because you specified duration longer than 3 months', null, 'success')
                }
                return {
                    info: data.data,
                    isEmailSent
                }
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function downloadAccountStatement(payload: TransactionPayload) {
      try {
        const { profileID, accountNumber, startDate, endDate, shouldSendMail } =
          payload;
        const constructBase = url.BASE_URL.endsWith("/")
          ? url?.BASE_URL
          : url?.BASE_URL + "/";
        const downloadUrl = `${constructBase}Account/DownloadAccountStatement?AccountNumber=${accountNumber}&ProfileID=${profileID}&StartDate=${startDate}&EndDate=${endDate}&shouldSendMail=${shouldSendMail}&authToken=${encodeURIComponent(
          helpers.hashString(info.authToken)
        )}&IBInstitutionCode=${institutionData.ibInstitutionCode}`;

        window.open(downloadUrl, "_blank");
      } catch (err) {
        loader(false);
        console.log("error", err);
        notifier.show(erroMessage(catchErrMsg(err)));
        return null;
      }
    }


    async function topupAirtime (payload: any) {
        try {
            const acctDetails = helpers.getAccountDetailsWithAcctNo(user.accountInfo, payload.accountNumber)

            loader(true)
            const {data} = await apiCaller.post('AirtimeTopUp/MakeRequest', {
                ...institutionData,
                ...payload,
                profileID: user.userInfo?.profileID,
                customerID: acctDetails?.customerID,
                accountName: acctDetails?.accountName,
                authenticationType: 0,
                amount: Number(payload.amount),
                reference: uuidv4()
            })
            loader(false)

            loglite({data}, 'topupAirtime API')
            if (data.responseCode === '3800') {
                return {
                    ...data.data,
                    shouldUseOtp: true
                }
            }
            else if (!data.isSuccessful && !data.data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                await getDashboardData()
                notifier.show(data.responseDescription || 'Successfully Purchased Airtime', null, 'success')
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getBanks (accountNumber: string) {
        try {
            //loader(true)
            const {data} = await apiCaller.post('Transfer/GetBanks', {
                ibInstitutionCode: institutionData.ibInstitutionCode,
                accountNumber
            })
            //loader(false)

            loglite({data}, 'getBanks API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            }
        }
        catch (err) {
            //loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }


    async function nameInquirySameBank (accountNumber: string, senderAccountNumber: string) {
        try {
            const acctDetails = helpers.getAccountDetailsWithAcctNo(user.accountInfo, senderAccountNumber)

            //loader(true)
            const {data} = await apiCaller.post('GetName/SameBank', {
                ...institutionData,
                deviceID: '',
                profileID: user.userInfo?.profileID,
                customerID: acctDetails?.customerID,
                accountNumber
            })
            //loader(false)

            loglite({data}, 'nameInquirySameBank API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            }
        }
        catch (err) {
            //loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function nameInquiryOtherBank (payload: any) {
        try {
            const acctDetails = helpers.getAccountDetailsWithAcctNo(user.accountInfo, payload.senderAccountNumber)

            //loader(true)
            const {data} = await apiCaller.post('GetName/OtherBank', {
                ...institutionData,
                deviceID: '',
                profileID: user.userInfo?.profileID,
                customerID: acctDetails?.customerID,
                beneficiaryAccountNumber: payload.beneficiaryAccountNumber,
                beneficiaryBankCode: payload.beneficiaryBankCode
            })
            //loader(false)

            loglite({data}, 'nameInquiryOtherBank API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            }
        }
        catch (err) {
            //loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getBeneficiaries () {
        try {
            //loader(true)
            const {data} = await apiCaller.post('Transfer/GetProfileBeneficiaries', {
                ...institutionData,
                profileID: user.userInfo?.profileID,
            })
            //loader(false)

            loglite({data}, 'getBeneficiaries API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                const beneficiaries = data.data //data.data.length ? data.data : mockBeneficiaries  // TODO: mockBeneficiaries should be removed as it is just for mocking
                return beneficiaries.filter((item: any) => !item.isDeleted)
            }
        }
        catch (err) {
            //loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function interbankTransfer (payload: any) {
        try {
            const acctDetails = helpers.getAccountDetailsWithAcctNo(user.accountInfo, payload.senderAccount)

            loader(true)
            const {data} = await apiCaller.post('Transfer/OtherBank', {
                ...institutionData,
                ...payload,
                deviceID: '',
                profileID: user.userInfo?.profileID,
                customerID: acctDetails?.customerID,
                senderName: acctDetails?.accountName,
                shouldSaveBeneficiary: true
                
            })
            loader(false)

            loglite({data}, 'interbankTransfer API')
            if (data.responseCode === '3800') {
                return {
                    ...data.data,
                    shouldUseOtp: true
                }
            }
            else if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                await getDashboardData()
                return data.data
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function intrabankTransfer (payload: any) {
        try {
            const acctDetails = helpers.getAccountDetailsWithAcctNo(user.accountInfo, payload.senderAccount)

            loader(true)
            const {data} = await apiCaller.post('Transfer/SameBank', {
                ...institutionData,
                ...payload,
                deviceID: '',
                profileID: user.userInfo?.profileID,
                customerID: acctDetails?.customerID,
                senderName: acctDetails?.accountName,
            })
            loader(false)

            loglite({data}, 'intrabankTransfer API')
            if (data.responseCode === '3800') {
                return {
                    ...data.data,
                    shouldUseOtp: true
                }
            }
            else if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                await getDashboardData()
                return data.data
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function scheduleTransfer (payload: any) {
        try {
            const acctDetails = helpers.getAccountDetailsWithAcctNo(user.accountInfo, payload.senderAccount)

            loader(true)
            const {data} = await apiCaller.post('Transfer/CreateScheduleTransfer', {
                ...institutionData,
                ...payload,
                deviceID: '',
                profileID: user.userInfo?.profileID,
                customerID: acctDetails?.customerID,
                senderName: acctDetails?.accountName,
                frequency: Number(payload.frequency),
                reminder: Number(payload.reminder)
            })
            loader(false)

            loglite({data}, 'scheduleTransfer API')
            if (data.responseCode === '3800') {
                return {
                    ...data.data,
                    shouldUseOtp: true
                }
            }
            else if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                await getNotifications()
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getTransactionByTxRef (transactionReference: string) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Transfer/GetTransactionByTransactionReference', {
                ...institutionData,
                profileID: user.userInfo?.profileID,
                transactionReference
            })
            loader(false)

            loglite({data}, 'getTransactionByTxRef API')
            if (!data.isSuccessful) {
                //notifier.show(requestMessage(data))
                return false
                //return  mockTransactionDetails  // TODO: mockTransactionDetails will be remove as it is just for testing
            }
            else return data.data
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function logFailedTransaction (payload: any) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Transfer/LogFailedTransaction', {
                ...institutionData,
                profileID: user.userInfo?.profileID,
                ...payload
            })
            loader(false)

            loglite({data}, 'logFailedTransaction API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(requestMessage(data, 'Failed Transaction Logged'), null, 'success')
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getAccountOfficer () {
        try {
            loader(true)
            const {data} = await apiCaller.post('AccountOfficer/GetAccountOfficer', {
                ibInstitutionCode: institutionData.ibInstitutionCode,
                profileID: user.userInfo?.profileID,
                accountNumber: user.userInfo?.profileInfo?.primaryAccount || user.accountInfo[0].accountNumber
            })
            loader(false)

            loglite({data}, 'getAccountOfficer API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                const rating = await getAccountOfficerRating()
                const accountOfficerInfo = data.data
                return {
                    rating,
                    details: accountOfficerInfo
                }
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getAccountOfficerRating () {
        try {
            loader(true)
            const {data} = await apiCaller.get('AccountOfficer/GetAccountOfficerRating', {
                params: {
                    accountNumber: user.userInfo?.profileInfo?.primaryAccount,
                    iBInstitutionCode: institutionData.ibInstitutionCode
                }
            })
            loader(false)

            loglite({data}, 'getAccountOfficerRating API')
            if (!data.isSuccessful) return false
            else return data.data
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function saveAccountOfficerRating (accountOfficerCode: string, ratingValue: number) {
        try {
            loader(true)
            const {data} = await apiCaller.post('AccountOfficer/SaveAccountOfficerRating', {
                accountNumber: user.userInfo?.profileInfo?.primaryAccount,
                profileID: user.userInfo?.profileID,
                accountOfficerCode,
                ratingValue,
                authToken: helpers.hashString(info.authToken)
            }, {
                params: {iBInstitutionCode: institutionData.ibInstitutionCode}
            })
            loader(false)

            loglite({data}, 'saveAccountOfficerRating API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Account Officer Rated', null, 'success')
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getTransactionLimit (accountNumber: string) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Profile/GetAccountTransactionLimit', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                profileID: user.userInfo?.profileID,
                accountNumber
            })
            loader(false)

            loglite({data}, 'getTransactionLimit API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return data.data
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function updateTransactionLimit (payload: any) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Profile/UpdateTransactionLimit', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                profileID: user.userInfo?.profileID,
                ...payload,
                dailyTransactionLimit: Number(payload.dailyTransactionLimit),
                oneTimeTransactionLimit: Number(payload.oneTimeTransactionLimit)
            })
            loader(false)

            loglite({data}, 'updateTransactionLimit API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Transaction Limit Changed', null, 'success')
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function setPanicPin (panicPin: string) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Profile/SetPanicPin', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                terminalType: 0,
                profileID: user.userInfo?.profileID,
                deviceID: '',
                panicPin
            })
            loader(false)

            loglite({data}, 'setPanicPin API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Panic PIN Set', null, 'success')
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getSecurityQuestionsByUsername (username: string) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Profile/GetSecurityQuestionsByUsername', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode || institutionData.ibInstitutionCode,
                terminalType: 0,
                deviceID: '',
                username: username || user.userInfo?.profileInfo?.username
            })
            loader(false)

            loglite({data}, 'getSecurityQuestionsByUsername API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else return data.data
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function validateSecurityAnswers (payload: any) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Profile/ValidateSecurityAnswers', {
                ibInstitutionCode: institutionData.ibInstitutionCode,
                terminalType: 0,
                deviceID: '',
                ...payload,
                securityAnswer1: helpers.hashString(payload.securityAnswer1),
                securityAnswer2: helpers.hashString(payload.securityAnswer2)
            })
            loader(false)

            loglite({data}, 'validateSecurityAnswers API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(`OTP has been sent to your registered number ${data.data?.phoneNo}`, 'OTP Status', 'success')
                return data.data
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function validateTxPin (username: string, txPin: string) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Profile/ValidateTransactionPin', {
                ibInstitutionCode: institutionData.ibInstitutionCode,
                terminalType: 0,
                deviceID: '',
                pin: helpers.hashString(txPin),
                username
            })
            loader(false)

            loglite({data}, 'validateTxPin API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(`OTP has been sent to your registered number ${data.data?.phoneNo}`, 'OTP Status', 'success')
                return data.data
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }


    async function resetPin (payload: any) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Profile/ResetPin', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                terminalType: 0,
                deviceID: '',
                profileID: user.userInfo?.profileID,
                ...payload,
                securityAnswer1: helpers.hashString(payload.securityAnswer1),
                securityAnswer2: helpers.hashString(payload.securityAnswer2),
                newPin: helpers.hashString(payload.newPin)
            })
            loader(false)

            loglite({data}, 'reset API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else return notifier.show(data.message || 'New PIN Set', null, 'success')
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function changePin_Password (payload: any, isPinChange = false) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Profile/ChangePin', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                terminalType: 0,
                deviceID: '',
                username: user.userInfo?.profileInfo?.username,
                oldPin: helpers.hashString(payload.oldPin),
                newPin: helpers.hashString(payload.newPin),
                isPinChange
            })
            loader(false)

            loglite({data}, 'changePin_Password API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || `${isPinChange ? 'PIN' : 'Password'} Changed Successfully`, null, 'success')
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function resetPassword (payload: any) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Profile/ResetPassword', {
                ...institutionData,
                ...payload,
                deviceID: '',
                otp: helpers.hashString(payload.otp),
                newPassword: helpers.hashString(payload.newPassword),
            })
            loader(false)

            loglite({data}, 'resetPassword API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else return notifier.show(data.message || 'Password was reset Successfully', null, 'success')
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function addBeneficiary (payload: any) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Transfer/AddBeneficiary', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                profileID: user.userInfo?.profileID,
                ...payload,
            })
            loader(false)

            loglite({data}, 'addBeneficiary API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return notifier.show(data.message || 'Beneficiary Added Successfully', null, 'success')
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function upgradeAccount (payload: AccountUpgrade) {
        try {
            loader(true)
            const {data} = await apiCaller.post('UpgradeAccount/RequestUpgradeAccount', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                profileID: user.userInfo?.profileID,
                ...payload,
            })
            loader(false)

            loglite({data}, 'upgradeAccount API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return notifier.show(data.message || 'Account Upgrade in Progress', null, 'success')
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getAccountUpgradeRequestStatus (accountNumber: string) {
        try {
            loader(true)
            const {data} = await apiCaller.post('UpgradeAccount/GetAccountUpgradeStatus', null, {
                params: {
                    ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                    profileID: user.userInfo?.profileID,
                    accountNumber
                }
            })
            loader(false)

            loglite({data}, 'getAccountUpgradeRequestStatus API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return data.data
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getScheduledTransfers (isCompleted: boolean) {
        try {
            //loader(true)
            const {data} = await apiCaller.post('Transfer/GetScheduleTransfersByProfileID', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                profileID: user.userInfo?.profileID,
                isCompleted
            })
            //loader(false)

            loglite({data}, 'getScheduledTransfers API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                const info = data.data // TODO: remove mockScheduledTransfers
                return info
            } 
        }
        catch (err) {
            //loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function cancelScheduledTransfer (id: number) {
        try {
            loader(true)
            const {data} = await apiCaller.delete(`Transfer/CancelScheduleTransfer/${user.userInfo?.profileInfo?.ibInstitutionCode}/${user.userInfo?.profileID}/${id}`)
            loader(false)

            loglite({data}, 'cancelScheduledTransfer API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return true
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getBillerCategories () {
        try {
            //loader(true)
            const {data} = await apiCaller.get(`BillsPayment/GetBillerCategories/${user.userInfo?.profileInfo?.ibInstitutionCode}`)
            //loader(false)

            loglite({data}, 'getBillerCategories API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            } 
        }
        catch (err) {
            //loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getBillers (billerCategoryId: number) {
        try {
            //loader(true)
            const {data} = await apiCaller.get(`BillsPayment/GetBillers/${user.userInfo?.profileInfo?.ibInstitutionCode}/${billerCategoryId}`)
            //loader(false)

            loglite({data}, 'getBillers API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            } 
        }
        catch (err) {
            //loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getDataBundleBillers () {
        try {
            //loader(true)
            const {data} = await apiCaller.get(`BillsPayment/GetDataBillers/${user.userInfo?.profileInfo?.ibInstitutionCode}`)
            //loader(false)

            loglite({data}, 'getDataBundleBillers API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            } 
        }
        catch (err) {
            //loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getPaymentPlans (billerId: number) {
        try {
            //loader(true)
            const {data} = await apiCaller.get(`BillsPayment/GetPaymentItems/${user.userInfo?.profileInfo?.ibInstitutionCode}/${billerId}`)
            //loader(false)

            loglite({data}, 'getPaymentPlans API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            } 
        }
        catch (err) {
            //loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function initiateBillsPayment (payload: any) {
        const acctDetails = helpers.getAccountDetailsWithAcctNo(user.accountInfo, payload.accountNumber)

        try {
            loader(true)
            const {data} = await apiCaller.post('BillsPayment/InitiatePayment', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                terminalType: 0,
                deviceID: '',
                ...payload,
                profileID: user.userInfo?.profileID,
                customerID: acctDetails?.customerID,
                customerName: acctDetails?.accountName,
                customerEmail: acctDetails?.emailAddress,
                customerPhoneNo: acctDetails?.phone,
                amount: Number(payload.amount),
                billerCategoryID: String(payload.billerCategoryID),
                billerID: String(payload.billerID)
            })
            loader(false)

            loglite({data}, 'initiateBillsPayment API')
            if (data.responseCode === '3800') {
                return {
                    ...data.data,
                    shouldUseOtp: true
                }
            }
            else if (!data.isSuccessful && !data?.data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                await getDashboardData()
                notifier.show(data.message || `${payload.paymentItemName} payment successful`, null, 'success')
                return true
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getInstitutionInfoForCard () {
        try {
            loader(true)
            const {data} = await apiCaller.post('Card/GetCardProfileCombo', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                profileID: user.userInfo?.profileID,
            })
            loader(false)

            loglite({data}, 'getInstitutionInfoForCard API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return data.data
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getCustomerCards () {
        try {
            loader(true)
            const {data} = await apiCaller.post('Card/GetAllCustomerCards', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                profileID: user.userInfo?.profileID,
            })
            loader(false)

            loglite({data}, 'getCustomerCards API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return data.data
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getCardOnAccount (accountNumber: string) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Card/GetCardOnAccount', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                profileID: user.userInfo?.profileID,
                accountNumber: accountNumber || user.userInfo?.profileInfo?.primaryAccount
            })
            loader(false)

            loglite({data}, 'getCardOnAccount API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return data.data
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getCardDeliveryOptions () {
        try {
            loader(true)
            const {data} = await apiCaller.post('Card/GetDeliveryOptions', {}, {
                params: {
                    IBInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                    ProfileID: user.userInfo?.profileID,
                }
            })
            loader(false)

            loglite({data}, 'getCardDeliveryOptions API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return data.data
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function logCardRequest (payload: any) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Card/LogCardRequest', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                profileID: user.userInfo?.profileID,
                ...payload,
                requestType: 'BATCHISSUANCE'
            })
            loader(false)

            loglite({data}, 'logCardRequest API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Card Request Sent', null, 'success')
                return true
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function hotlistCard (payload: any) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Card/HotlistCard', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                profileID: user.userInfo?.profileID,
                ...payload
            })
            loader(false)

            loglite({data}, 'hotlistCard API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Card Hotlisted', null, 'success')
                return true
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getNotifications (shouldLoad = true) {
        try {
            if(shouldLoad) loader(true)
            const {data} = await apiCaller.get(`Notification/GetNotificationsByProfileID`, {
                params: {
                    IBInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                    ProfileID: user.userInfo?.profileID
                }
            })
            if (shouldLoad) loader(false)

            loglite({data}, 'getNotifications API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                setUserProperty('notifications', data.data) // TODO: mockNotifications will be remove as it is just for testing
                return data.data
            }
        }
        catch (err) {
            if(shouldLoad) loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function deleteBeneficiary (beneficiaryID: number) {
        try {
            //loader(true)
            const {data} = await apiCaller.post('Transfer/RemoveBeneficiary', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                profileID: user.userInfo?.profileID,
                beneficiaryID,
            })
            //loader(false)

            loglite({data}, 'deleteBeneficiary API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return true
        }
        catch (err) {
            //loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getExpenseCategories () {
        try {
            const {data} = await apiCaller.get('Transfer/GetExpenseCategories', {
                params: {
                    ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                    profileID: user.userInfo?.profileID
                }
            })

            loglite({data}, 'getExpenseCategories API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return null
            }
            else return data.data
        }
        catch (err) {
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getInstitutionData (alias: string) {
        try {
            loader(true)
            const {data} = await apiCaller.get('Institution/api/1.1/Institution/GetInstitutionByAlias', {
                params: {alias}
            })
            loader(false)

            loglite({data}, 'getInstitutionData API')
            if (!data.isSuccessful) {
                return false
            }
            else return data.data
        }
        catch (err) {
            loader(false)
            return null
        }
    }

    async function submitTransferWithAuth (payload: any) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Transfer/SubmitTransferOTP', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                profileID: user.userInfo?.profileID,
                ...payload,
            })
            loader(false)

            loglite({data}, 'submitTransferWithAuth API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                await getDashboardData()
                return data.data
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function submitScheduleTransferWithAuth (payload: any) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Transfer/SubmitScheduledTransferOTP', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                profileID: user.userInfo?.profileID,
                terminalType: institutionData.terminalType,
                ...payload,
            })
            loader(false)

            loglite({data}, 'submitScheduleTransferWithAuth API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                await getDashboardData()
                return data.data
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function submitAirtimeTopupWithAuth (payload: any) {
        try {
            loader(true)
            const {data} = await apiCaller.post('AirtimeTopUp/SubmitAirtimeTopupOTP', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                profileID: user.userInfo?.profileID,
                ...payload,
            })
            loader(false)

            loglite({data}, 'submitAirtimeTopupWithAuth API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                await getDashboardData()
                notifier.show(data.responseDescription || 'Successfully Purchased Airtime', null, 'success')
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function submitBillsPaymentWithAuth (payload: any) {
        try {
            loader(true)
            const {data} = await apiCaller.post('BillsPayment/SubmitBillPaymentOTP', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                profileID: user.userInfo?.profileID,
                ...payload,
            })
            loader(false)

            loglite({data}, 'submitBillsPaymentWithAuth API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                await getDashboardData()
                notifier.show(data.responseDescription || 'Bills Payment Successful', null, 'success')
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getOnboardingSideContent (institutionId?: number) {
        try {
            loader(true)
            const {data} = await apiCaller.get('Onboarding/GetOnboardingSideInfo', {
                params: {IBInstitutionCode: institutionId || institutionData.ibInstitutionCode}
            })
            loader(false)

            loglite({data}, 'getOnboardingSideContent API')
            if (!data.isSuccessful) {
                return false
            }
            else {
                await setInfoProperty('onboardingSideInfo', data.data)
                return data.data
            }
        }
        catch (err) {
            loader(false)
            return null
        }
    }

    async function getTermsAndConditions_Privacy (institutionId?: number) {
        try {
            loader(true)
            const {data} = await apiCaller.get('Onboarding/GetPrivacyInfo', {
                params: {IBInstitutionCode: institutionId || institutionData.ibInstitutionCode}
            })
            loader(false)

            loglite({data}, 'getTermsAndConditions_Privacy API')
            if (!data.isSuccessful) {
                return false
            }
            else {
                await setInfoProperty('termsAndConditions_Privacy', data.data)
                return data.data
            }
        }
        catch (err) {
            loader(false)
            return null
        }
    }

    async function resendTransactionOtp () {
        try {
            loader(true)
            const {data} = await apiCaller.post('Transfer/ResendTransactionOtp', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                profileID: user.userInfo?.profileID
            })
            loader(false)

            loglite({data}, 'resendTransactionOtp API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show('Otp successfully sent', null, 'success')
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getFrequentAirtime () {
        try {
            const {data} = await apiCaller.post('AirtimeTopUp/GetFrequentAirtimeAmounts', {}, {
                params: {
                    ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                    profileID: user.userInfo?.profileID,
                }
            })

            loglite({data}, 'getFrequentAirtime API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return data.data
        }
        catch (err) {
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getCardRequestStatus (accountNumber: string) {
        try {
            const {data} = await apiCaller.post('Card/GetCardRequestStatus', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                profileID: user.userInfo?.profileID,
                accountNumber
            })

            loglite({data}, 'getCardRequestStatus API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return data.data
        }
        catch (err) {
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getInstitution() {
        const {hostName, mfb} = await utility.extractMfb() as any
        const savedHostName = await localStorage.getItem('hostName')
        if (savedHostName && savedHostName != hostName) await logout()
        
        const response = await getInstitutionData(mfb)
        const institutionInfo = {...response?.institution, featureIDs: response?.featureIDs, webActiveFeed: response?.webActiveFeed, onboardingFlow: response?.institution?.newCustomerOnboardingType}
 
        if (response) {
            if (!institutionInfo.design) {
                institutionInfo.design = 1
            }
            
            const theme = helpers.extractInstitutionTheme(institutionInfo)
            await Promise.all([
                setInfoProperty('theme', theme),
                setInfoProperty('institutionData', institutionInfo),
                setInfoProperty('hostName', hostName),
                getOnboardingSideContent(institutionInfo.id),
                getTermsAndConditions_Privacy(institutionInfo.id)
            ])
        }
        else {
            Promise.all([
                setInfoProperty('theme', {}),
                setInfoProperty('institutionData', {})
            ])
        }
    }

    async function updateUsernameAndSecurityAnswers (payload: any) {
        try {
            loader(true)
            const {data} = await apiCaller.post('Profile/UpdateUsernameAndSecurityAnswers', {
                ibInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                profileID: user.userInfo?.profileID,
                terminalType: institutionData.terminalType,
                deviceID: '',
                ...payload,
                securityAnswer1: helpers.hashString(payload?.securityAnswer1),
                securityAnswer2: helpers.hashString(payload?.securityAnswer2)
            })
            loader(false)

            loglite({data}, 'updateUsernameAndSecurityAnswers API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show('Security Questions and Answers Successfully Updated', null, 'success')
                return data.isSuccessful
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getFixedDeposit () {
        try {
            loader(true)
            const {data} = await apiCaller.get(`Investment/GetFixedDepositsByProfileID`, {
                params: {
                    IBInstitutionCode: user.userInfo?.profileInfo?.ibInstitutionCode,
                    profileID: user.userInfo?.profileID
                }
            })
            loader(false)

            loglite({data}, 'getFixedDeposit API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getProducts () {
        try {
            const {data} = await apiCaller.get(`Product/GetProducts/${user.userInfo?.profileInfo?.ibInstitutionCode}?discriminator=FixedDeposit`)

            loglite({data}, 'getProducts API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            } 
        }
        catch (err) {
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function initiateFixedDeposit (payload: any) {
        try {
            loader(true)
            const {data} = await apiCaller.post(`Investment/InitiateFixedDeposit`, {
                ...institutionData,
                profileID: user.userInfo?.profileID,
                ...payload
            })
            loader(false)

            loglite({data}, 'initiateFixedDeposit API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                await getDashboardData()
                notifier.show(data?.responseDescription || 'Investment Confirmed', null, 'success')
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function topupFixedDeposit (payload: any) {
        try {
            loader(true)
            const {data} = await apiCaller.post(`Investment/TopupFixedDeposit`, {
                ...institutionData,
                profileID: user.userInfo?.profileID,
                ...payload
            })
            loader(false)

            loglite({data}, 'topupFixedDeposit API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                await getDashboardData()
                notifier.show(data?.responseDescription || 'Funds Added', null, 'success')
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function liquidateFixedDeposit (payload: any) {
        try {
            loader(true)
            const {data} = await apiCaller.post(`Investment/LiquidateFixedDeposit`, {
                ...institutionData,
                profileID: user.userInfo?.profileID,
                ...payload,
                liquidationType: Number(payload.liquidationType)
            })
            loader(false)

            loglite({data}, 'liquidateFixedDeposit API')
            if (data.responseCode != '00') {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                await getDashboardData()
                notifier.show(data?.responseDescription || 'Funds Liquidated', null, 'success')
                return data.data
            } 
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getExpenseCategorySummary (payload: any) {
        try {
            loader(true)
            const {data} = await apiCaller.post(`ExpenseCategory/GetExpenseCategorySummary`, {
                ...institutionData,
                profileID: user.userInfo?.profileID,
                ...payload
            })
            loader(false)

            loglite({data}, 'getExpenseCategorySummary API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return data.data
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getMonthlyExpense (accountNumber: string) {
        try {
            loader(true)
            const {data} = await apiCaller.get(`ExpenseCategory/GetMonthlyExpense`, {
                params: {
                    ...institutionData,
                    profileID: user.userInfo?.profileID,
                    accountNumber
                }
            })
            loader(false)

            loglite({data}, 'getMonthlyExpense API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return data.data
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getInsightTransactions (payload: TransactionPayload) {
        try {
            loader(true)
            const {data} = await apiCaller.post(`ExpenseCategory/GetExpenseCategoryTransactions?IBInstitutionCode=${user.userInfo?.profileInfo?.ibInstitutionCode}`, {
                profileID: user.userInfo?.profileID,
                ...payload 
            })
            loader(false)

            loglite({data}, 'getInsightTransactions API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return data.data
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function resendOtp (uniqueId: string) {
        try {
            loader(true)
            const {data} = await apiCaller.post(`Onboarding/ResendOtp`, {
                ...institutionData,
                customerID: uniqueId
            })
            loader(false)

            loglite({data}, 'resendOtp API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return notifier.show('Otp Successfully Sent', null, 'success')
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function updateProfilePics (payload: any) {
        try {
            loader(true)
            const {data} = await apiCaller.post(`Profile/UpdateProfilePicture`, {
                ...institutionData,
                profileID: user.userInfo?.profileID,
                ...payload
            })
            loader(false)

            loglite({data}, 'updateProfilePics API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show(data.message || 'Profile Picture Updated Successfully', null, 'success')
                return data.data
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function computeFixedDepositData (payload: any) {
        try {
            const {data} = await apiCaller.post(`Investment/ComputeFixedDepositDetails`, {
                ...institutionData,
                profileID: user.userInfo?.profileID,
                ...payload
            })

            loglite({data}, 'computeFixedDepositData API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return data.data
        }
        catch (err) {
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getAccruedInterest (fixedDepositAccountNumber: string) {
        try {
            const {data} = await apiCaller.post(`Investment/GetFixedDepositAccruedInterest`, {
                ...institutionData,
                profileID: user.userInfo?.profileID,
                fixedDepositAccountNumber
            })

            loglite({data}, 'getAccruedInterest API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return data.data
        }
        catch (err) {
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function deactivateProfile (otp: string) {
        try {
            const {username, reason} = user.acctDeletionData

            loader(true)
            const {data} = await apiCaller.post(`Profile/DeactivateProfile`, {
                ...institutionData,
                otp,
                username,
                reason
            })
            loader(false)

            loglite({data}, 'deactivateProfile API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else return true
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function sendProfileDeactivationOtp () {
        try {
            const {accountNumber, username} = user.acctDeletionData

            loader(true)
            const {data} = await apiCaller.post(`Profile/SendProfileDeactivationOtp`, {
                ...institutionData,
                username,
                accountNumber
            })
            loader(false)

            loglite({data}, 'sendProfileDeactivationOtp API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                notifier.show('An OTP has been sent to your email/number', null, 'success')
                return true
            }
        }
        catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getEbanqoSupport () {
        try {
            loader(true)
            const {data} = await apiCaller.get(`Support/Ebanqo`, {
                params: {
                    IBInstitutionCode: info.institutionData?.id,
                    username: user?.userInfo?.profileInfo?.username
                }
            })
            loader(false)

            loglite({data}, 'getEbanqoSupport API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return null
            }
            else {
                return data.data
            }
        }
        catch (err) {
            loader(false)
            //notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getLoanProducts () {
        if(institutionData?.ibInstitutionCode)
        try {
            loader(true)
            const {data} = await apiCaller.get(`Loan/GetLoanProducts`, {
                params: {IBInstitutionCode: 1}
            })
            loader(false)

            loglite({data}, 'getLoanProducts API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return null
            }
            else {
                return data.data
            }
        } catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getLoans () {
        if(institutionData?.ibInstitutionCode)
        try {
            loader(true)
            const {data} = await apiCaller.get(`Loan/GetLoansByProfileID`, {
                params: {
                    IBInstitutionCode: 1,
                    profileID:user?.userInfo?.profileID
                }
            })
            loader(false)

            loglite({data}, 'getLoans API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return null
            }
            else {
                return data.data
            }
        } catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getLoanDetails ({LoanAccountNumber}:{LoanAccountNumber: string}) {
        if(institutionData?.ibInstitutionCode)
        try {
            loader(true)
            const {data} = await apiCaller.get(`Loan/GetLoanDetails`, {
                params: {
                    IBInstitutionCode: 1,
                    profileID:user?.userInfo?.profileID,
                    LoanAccountNumber,
                }
            })
            loader(false)

            loglite({data}, 'getLoanDetails API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return null
            }
            else {
                return data.data
            }
        } catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getLoanRepaymentSchedule ({LoanAccountNumber}:{LoanAccountNumber: string}) {
        if(institutionData?.ibInstitutionCode)
        try {
            loader(true)
            const {data} = await apiCaller.get(`Loan/GetLoanRepaymentSchedule`, {
                params: {
                    IBInstitutionCode: 1,
                    profileID:user?.userInfo?.profileID,
                    LoanAccountNumber,
                }
            })
            loader(false)

            loglite({data}, 'getLoanRepaymentSchedule API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return null
            }
            else {
                return data.data
            }
        } catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function repayLoan (input:{
        loanAccountNumber: string,
        repaymentAmount: number | undefined,
        isTermination: boolean,
        pin: string}) {

        if(institutionData?.ibInstitutionCode)
        try {
            loader(true)
            const {data} = await apiCaller.post(`Loan/RepayLoan`, {
                ibInstitutionCode: 1,
                profileId:user?.userInfo?.profileID,
                ...input
            })
            loader(false)

            loglite({data}, 'repayLoan API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return null
            }
            else {
                return data.data
            }
        } catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function runCreditAssessment (input:any) {
        if(institutionData?.ibInstitutionCode)
        try {
            loader(true)
            const {data} = await apiCaller.post(`Loan/RunCreditAssessment`, {
                ibInstitutionCode: 1,
                profileId: user?.userInfo?.profileID,
                customerName: user?.userInfo?.profileInfo?.firstName + ' ' + user?.userInfo?.profileInfo?.lastName,
                ...input,
            })
            loader(false)

            loglite({data}, 'runCreditAssessment API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return null
            }
            else {
                return data.data
            }
        } catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function createConsent (input:any) {
        if(institutionData?.ibInstitutionCode)
        try {
            loader(true)
            const {data} = await apiCaller.post(`Loan/CreateConsent`, {
                ibInstitutionCode: 1,
                profileId: user?.userInfo?.profileID,
                ...input,
            })
            loader(false)

            loglite({data}, 'createConsent API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return null
            }
            else {
                return data.data
            }
        } catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getFaq() {
        if(institutionData?.ibInstitutionCode)
        try {
            loader(true)
            const {data} = await apiCaller.get(`FAQ`, {
                params: {
                    IBInstitutionCode: institutionData?.ibInstitutionCode
                }
            })
            loader(false)

            loglite({data}, 'getFaq API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return null
            }
            else {
                return data.data
            }
        } catch (err) {
            loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    async function getRFSAirtimeBillers () {
        try {
            //loader(true)
            const {data} = await apiCaller.get(`BillsPayment/GetRFSAirtimeBillers/${user.userInfo?.profileInfo?.ibInstitutionCode}`)
            //loader(false)

            loglite({data}, 'getRFSAirtimeBillers API')
            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }
            else {
                return data.data
            } 
        }
        catch (err) {
            //loader(false)
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }

}

async function getCbpProfile () {
    try {
        // loader(true)
        const {data} = await apiCaller.get(`cbp/Profile/Self`)
        // loader(false)

        loglite({data}, 'getCbpProfile API')
        if (!data.isSuccessful) {
            notifier.show(requestMessage(data))
            return false
        }
        else {
            return data
        }
    }
    catch (err) {
        loader(false)
        notifier.show(erroMessage(catchErrMsg(err)))
        return null
    }

}

async function getCbpDocs () {
    try {
        // loader(true)
        const {data} = await apiCaller.get(`cbp/Profile/compliance-docs`)
        // loader(false)

        loglite({data}, 'getCbpDocs API')
        if (!data.isSuccessful) {
            notifier.show(requestMessage(data))
            return false
        }
        else {
            return data
        }
    }
    catch (err) {
        loader(false)
        notifier.show(erroMessage(catchErrMsg(err)))
        return null
    }

}

async function createCbpProfile (payload:FormData) {
    try {
        loader(true)
        const {data} = await apiCaller.post(`cbp/Profile`, payload,{
            headers: {
                "Content-Type": "multipart/form-data",
              },
        })
        console.log("cbppp", data)

        loader(false)

        loglite({data}, 'createCbpProfile API')
        if (!data.isSuccessful) {
            notifier.show(requestMessage(data))
            return false
        }
        else {
            return data
        }
    }
    catch (err) {
        loader(false)
        notifier.show(erroMessage(catchErrMsg(err)))
        return null
    }

}

    const callActions = {
        validateNewCustomerBvn,
        validateReturningCustomerBvn,
        getNewCustomerData,
        getReturningCustomerData,
        verifyIdentity,
        getSecurityQuestions,
        onboardCustomer,
        login,
        authenticateLoginOtp,
        getDashboardData,
        getTransactions,
        topupAirtime,
        getBanks,
        nameInquirySameBank,
        nameInquiryOtherBank,
        interbankTransfer,
        intrabankTransfer,
        scheduleTransfer,
        getBeneficiaries,
        getTransactionByTxRef,
        logFailedTransaction,
        getAccountOfficer,
        getAccountOfficerRating,
        saveAccountOfficerRating,
        getTransactionLimit,
        updateTransactionLimit,
        setPanicPin,
        getSecurityQuestionsByUsername,
        validateSecurityAnswers,
        validateTxPin,
        resetPin,
        changePin_Password,
        resetPassword,
        addBeneficiary,
        upgradeAccount,
        getScheduledTransfers,
        cancelScheduledTransfer,
        getBillerCategories,
        getBillers,
        getDataBundleBillers,
        getPaymentPlans,
        initiateBillsPayment,
        getInstitutionInfoForCard,
        getCustomerCards,
        getCardOnAccount,
        getCardDeliveryOptions,
        logCardRequest,
        hotlistCard,
        getNotifications,
        deleteBeneficiary,
        getExpenseCategories,
        getInstitutionData,
        submitTransferWithAuth,
        submitScheduleTransferWithAuth,
        submitAirtimeTopupWithAuth,
        submitBillsPaymentWithAuth,
        getOnboardingSideContent,
        getTermsAndConditions_Privacy,
        resendTransactionOtp,
        getFrequentAirtime,
        getCardRequestStatus,
        getInstitution,
        updateUsernameAndSecurityAnswers,
        getFixedDeposit,
        getProducts,
        initiateFixedDeposit,
        topupFixedDeposit,
        liquidateFixedDeposit,
        getExpenseCategorySummary,
        getInsightTransactions,
        getMonthlyExpense,
        resendOtp,
        updateProfilePics,
        computeFixedDepositData,
        getAccruedInterest,
        deactivateProfile,
        sendProfileDeactivationOtp,
        getAccountUpgradeRequestStatus,
        getEbanqoSupport,
        getLoanProducts,
        getLoans,
        getLoanDetails,
        getLoanRepaymentSchedule,
        repayLoan,
        runCreditAssessment,
        createConsent,
        validateNonNGNewCustomer,
        validateNonNGReturningCustomer,
        getNonNGNewCustomerData,
        onboardNonNGNewCustomer,
        getFaq,
        getRFSAirtimeBillers,
        downloadAccountStatement,
        getCbpProfile,
        getCbpDocs,
        createCbpProfile
    }

    return (
        <ApiContext.Provider value={{API: callActions}} >
            {props.children}
        </ApiContext.Provider>
    )
}

export default ApiContext